import React, { useEffect, useState } from 'react'
import Image from 'next/image'
import { IoCloseCircleSharp } from 'react-icons/io5'
import { Button, Skeleton, Spinner } from "@heroui/react"

import { getSignedUrl, uploadToS3 } from './upload'

const UploadedImage = ({ img, remove }) => {
  const [ready, setReady] = useState(false)
  
  return <Skeleton className='rounded-lg' isLoaded={ready} key={img.key}>
    <div className={`rounded-md relative aspect-[4/3]`}>
      {ready && <Button
        isIconOnly
        className='absolute right-1 top-1 bg-white rounded-full w-auto z-10'
        onPress={() => {
          remove(img.key)
        }}
        ><IoCloseCircleSharp className='shadow-md text-black text-4xl' /></Button>}
      
      {img.uploaded && <Image 
        key={img.key}  
        src={img.url} 
        alt='uploaded image' 
        width={0} height={0}
        sizes="(max-width: 768px) 100vw, 50vw"
        fill
        className={`object-cover rounded-md`}
        onLoad={() => setReady(true)} />}
    </div>
  </Skeleton>
}

const UploadPanel = ({ uploadRef, images = new Map(), setImages, user }) => {
  const [error, setError] = useState('')
  const [uploading, setUploading] = useState(false)
  const [showImgPanel, setShowImgPanel] = useState(false)
  
  useEffect(() => {
    if (images.size > 0) {
      setShowImgPanel(true)
    } else {
      setShowImgPanel(false)
    }
  }, [images])

  const onUploadChange = async (e) => {
    e.preventDefault()
    e.stopPropagation()
    
    if (e.target.files.length > 4 - images.size) {
      setError('You can upload a maximum of 4 images.')
      return 
    }
    
    setUploading(true)
    
    try {
      const uploadedImages = new Map(images);
      
      // Process files sequentially
      for (let index = 0; index < e.target.files.length; index++) {
        const file = e.target.files[index];

        if (!file || !file.type) {
          setError('Invalid file.')
          setUploading(false)
          return
        }

        let fileType = file.type.slice(file.type.lastIndexOf('/') + 1).toLowerCase()
        const fileParts = file.name.split('.')
        const contentType = fileParts[fileParts.length - 1].toLowerCase()
        
        if (['jpg', 'png', 'jpeg', 'gif'].indexOf(contentType) === -1) {
          setError('Invalid file type (jpg, png, or gif)')
          setUploading(false)
          return
        }

        const data = await getSignedUrl(user.token, file.name, fileType, contentType)
        
        // Create image entry
        uploadedImages.set(data.key, {
          key: data.key,
          url: data.url,
          fileType,
          contentType,
          uploaded: false
        })
        
        // Update state with current progress
        setImages(new Map(uploadedImages))
        await uploadToS3(data.signedRequest, fileType, file)
        
        // Update the uploaded status for this image
        uploadedImages.set(data.key, {
          key: data.key,
          url: data.url,
          fileType,
          contentType,
          uploaded: true
        })
        setImages(new Map(uploadedImages))
        setError('')
      }
    
    } catch (err) {
      console.error('Error during upload:', err)
      setUploading(false)
      setError('Error uploading one or more images')
    } finally {
      setUploading(false)
    }
  }

  return <>
    {error && <div className='text-sm text-danger mb-1'>{error}</div>}
    <div className={`grid grid-cols-2 gap-2 rounded-md bg-chTan-300 transition-height flex-wrap overflow-x-scroll ${showImgPanel ? 'min-h-[100px] p-4 mb-1' : 'h-0 p-0'}`}>
      {(!error && !images.size) && <Skeleton className='rounded-lg' isLoaded={false}>
        <div className='w-[200px] h-[150px] bg-chTan-400 rounded-md'>&nbsp;</div>
      </Skeleton>}
      {[ ...images.entries() ].map(([key, val]) => {
        return <UploadedImage img={val} key={key} totalImages={images.size} remove={() => {
          const newImgs = new Map(images)
          newImgs.delete(key)
          setImages(newImgs)
          if (newImgs.size === 0) {
            setShowImgPanel(false)
          }
        }} />
      })}
      <input
        ref={uploadRef}
        type='file'
        multiple
        onChange={onUploadChange} 
        className='hidden h-0 absolute'/>
    </div>
  </>
}

export default UploadPanel
